import api from '../api'
import { getCurrentClinic } from '../../../utils/localStorageManager'
import moment from 'moment'

const clinic = getCurrentClinic()

function backfillOFXBankAccountName(context) {
  const id = context.state.ofxForm?.bank_account_id
  if (id == null) return
  if (context.state.ofxForm.bank_account_name) return
  const accounts = context.state.clinicBankAccounts || []
  const found = accounts.find(a => String(a.id) === String(id))
  if (!found?.name) return
  context.commit('setOFXFormField', {
    field: 'bank_account_name',
    value: found.name
  })
  if (context.state.importedOFX) {
    persistReconciliationSession(context)
  }
}

const getClinicBankAccounts = context => {
  context.commit('setLoading', true)
  api
    .getBankAccounts(clinic.id)
    .then(({ data }) => {
      const bankAccounts = data.map(bankAccount => ({
        ...bankAccount,
        label: bankAccount.name,
        value: bankAccount.id
      }))

      context.commit('setClinicBankAccounts', bankAccounts)
      backfillOFXBankAccountName(context)
    })
    .finally(() => context.commit('setLoading', false))
}

const getClinicPaymentMethods = context => {
  api.getPaymentMethodsBillToReceive(clinic.id).then(({ data }) => {
    context.commit('setClinicPaymentMethods', data)
  })
}

const importOFX = (context, data) => {
  context.commit('setLoading', true)
  data.period.start = moment(data.period.start).format('YYYY-MM-DD')
  data.period.end = moment(data.period.end).format('YYYY-MM-DD')
  return api
    .importOFX(data)
    .then(({ data: responseData }) => {
      if (data.bank_account_id != null) {
        context.commit('setOFXFormField', {
          field: 'bank_account_id',
          value: data.bank_account_id
        })
      }
      if (data.bank_account_name) {
        context.commit('setOFXFormField', {
          field: 'bank_account_name',
          value: data.bank_account_name
        })
      }
      const periodStart = responseData.start ?? data.period?.start ?? null
      const periodEnd = responseData.end ?? data.period?.end ?? null
      const importedPayload = {
        ...responseData,
        start: periodStart,
        end: periodEnd
      }
      context.commit('setImportedOFX', importedPayload)
      context.commit('setReconciliationStatFilter', 'pending_divergence')
      context.commit('resetReconciliationPairingUi')
      context.commit('setArchivedFromExtract', [])
      context.commit('setArchivedEntriesFromApi', [])
      if (periodStart && periodEnd) {
        context.commit('setOFXFormPeriod', {
          start: periodStart,
          end: periodEnd
        })
      }
      const summary = responseData.summary

      const reconciledAutomaticallyCount = Number(summary.matched_auto) || 0
      const totalMatched = Number(summary.matched) || 0
      const manualMatched = Math.max(
        totalMatched - reconciledAutomaticallyCount,
        0
      )
      const revisionsCount = Number(summary.unmatched_imported) || 0
      const alreadyReconciledCount = Number(summary.already_reconciled) || 0

      context.commit('setReconciliationCounts', {
        revisionsCount,
        reconciledAutomaticallyCount,
        matchedCount: manualMatched,
        finalizedCount: alreadyReconciledCount
      })
      context.commit('setArchivedCount', Number(summary.archived) || 0)

      const accId = context.state.ofxForm?.bank_account_id
      if (accId != null && !context.state.ofxForm.bank_account_name) {
        const acc = (context.state.clinicBankAccounts || []).find(
          a => String(a.id) === String(accId)
        )
        if (acc?.name) {
          context.commit('setOFXFormField', {
            field: 'bank_account_name',
            value: acc.name
          })
        }
      }

      persistReconciliationSession(context)
    })
    .catch(error => {
      context.commit(
        'setError',
        error?.response?.data?.message ||
          error?.message ||
          'Erro ao importar OFX'
      )
      throw error
    })
    .finally(() => {
      context.commit('setLoading', false)
    })
}

async function getTransactions(context, filtersPayload = null) {
  context.commit('setLoading', true)
  const filters =
    filtersPayload != null ? filtersPayload : context.state.filters
  try {
    const response = await api.getReconciliationTransactions(
      clinic.id,
      context.state.transactionsPage,
      filters
    )
    context.commit('setTransactions', response.data.transactions)
    context.commit('setTransactionsCount', response.data.pagination.total)
    if (!context.state.isAutomaticReconciliation) {
      context.commit('setRevisionsCount', response.data.pagination.total)
    }
    context.commit('setTransactionsLimit', response.data.pagination.per_page)
    context.commit(
      'setTransactionsLastPage',
      response.data.pagination.last_page
    )
    context.commit('setTransactionsPage', response.data.pagination.current_page)
    context.commit('setSumToPay', response.data.sumToPay)
    context.commit('setSumToReceive', response.data.sumToReceive)
  } catch (error) {
    context.commit('setError', error.message)
  } finally {
    context.commit('setLoading', false)
  }
}

async function reconcileTransactions(context, options = {}) {
  context.commit('setLoading', true)
  const user = JSON.parse(localStorage.getItem('user'))
  const tabId = window.sessionStorage.getItem('tabId') || `tab_${Date.now()}`
  const selected = context.state.selectedTransactions || []
  // Seleções pequenas rodam de forma síncrona (resposta e atualização
  // imediatas); lotes grandes seguem em background com feedback otimista + Pusher.
  const SYNC_MAX_ITEMS = 5
  const payload = {
    clinic_id: clinic.id,
    user_id: user.id,
    transactions: selected,
    tab_id: tabId,
    run_in_background: selected.length > SYNC_MAX_ITEMS,
    // Override da trava de valor do backend (conciliação parcial confirmada).
    allow_value_divergence: !!options.allowValueDivergence,
    reconciliation_type: context.state.isAutomaticReconciliation
      ? 'automatic'
      : 'manual'
  }

  try {
    const response = await api.reconcileTransactions(payload)
    const queued =
      response?.status === 202 || response?.data?.status === 'queued'
    context.commit('clearSelectedTransactions')
    // Sempre refaz a listagem: se síncrono, traz o resultado final; se em
    // background, reflete o estado 'processing' que o backend já gravou.
    await context.dispatch('getTransactions')
    return {
      queued,
      response: response?.data || null
    }
  } catch (error) {
    context.commit('setError', error.message)
    throw error
  } finally {
    context.commit('setLoading', false)
  }
}

async function unreconcileTransactions(context) {
  context.commit('setLoading', true)
  const user = JSON.parse(localStorage.getItem('user'))
  const payload = {
    clinic_id: clinic.id,
    user_id: user.id,
    transactions: context.state.selectedTransactions
  }
  try {
    await api.unreconcileTransactions(payload)
    context.commit('clearSelectedTransactions')
    context.dispatch('getTransactions')
  } catch (error) {
    context.commit('setError', error.message)
  } finally {
    context.commit('setLoading', false)
  }
}

// OFX Form actions
function updateOFXFormField(context, { field, value }) {
  context.commit('setOFXFormField', { field, value })
}

function updateOFXFormPeriod(context, { start, end }) {
  context.commit('setOFXFormPeriod', { start, end })
}

function updateOFXFormFile(context, file) {
  context.commit('setOFXFormFile', file)
}

function resetOFXForm(context) {
  context.commit('resetOFXForm')
}

function initializeOFXForm(context, clinicId) {
  context.commit('initializeOFXForm', clinicId)
}

function setIsAutomaticReconciliation(context, isAutomaticReconciliation) {
  context.commit('setIsAutomaticReconciliation', isAutomaticReconciliation)
}

// Persistence helpers
const RECONCILIATION_SESSION_KEY = 'reconciliationSession'

function persistReconciliationSession(context) {
  try {
    const payload = {
      importedOFX: context.state.importedOFX,
      filters: context.state.filters,
      isAutomaticReconciliation: !!context.state.isAutomaticReconciliation,
      finalizedCount: context.state.finalizedCount,
      archivedCount: context.state.archivedCount,
      reconciliationStatFilter: context.state.reconciliationStatFilter,
      reconciliationPairingUi: context.state.reconciliationPairingUi,
      archivedFromExtract: context.state.archivedFromExtract || [],
      ofxForm: {
        bank_account_id: context.state.ofxForm.bank_account_id,
        bank_account_name: (() => {
          const id = context.state.ofxForm.bank_account_id
          const cached = context.state.ofxForm.bank_account_name
          if (cached) return cached
          if (id == null) return null
          const accounts = context.state.clinicBankAccounts || []
          const found = accounts.find(a => String(a.id) === String(id))
          return found?.name ?? null
        })(),
        period: {
          start: context.state.ofxForm.period.start,
          end: context.state.ofxForm.period.end
        }
      }
    }
    localStorage.setItem(RECONCILIATION_SESSION_KEY, JSON.stringify(payload))
  } catch (e) {
    console.error('Erro ao persistir sessão de conciliação:', e)
  }
}

function restoreReconciliationSession(context) {
  try {
    const raw = localStorage.getItem(RECONCILIATION_SESSION_KEY)
    if (!raw) return false
    const session = JSON.parse(raw)
    if (!session || !session.importedOFX) return false

    context.commit('setImportedOFX', session.importedOFX)
    if (session.filters) {
      context.commit('setFilters', session.filters)
    }
    // Restore ofxForm data if it exists in the session
    if (session.ofxForm) {
      if (session.ofxForm.bank_account_id) {
        context.commit('setOFXFormField', {
          field: 'bank_account_id',
          value: session.ofxForm.bank_account_id
        })
      }
      if (session.ofxForm.bank_account_name) {
        context.commit('setOFXFormField', {
          field: 'bank_account_name',
          value: session.ofxForm.bank_account_name
        })
      }
      if (session.ofxForm.period?.start && session.ofxForm.period?.end) {
        context.commit('setOFXFormPeriod', {
          start: session.ofxForm.period.start,
          end: session.ofxForm.period.end
        })
      }
    }
    const restoredIsAutomatic =
      typeof session.isAutomaticReconciliation === 'boolean'
        ? session.isAutomaticReconciliation
        : true
    context.commit('setIsAutomaticReconciliation', restoredIsAutomatic)
    if (typeof session.finalizedCount === 'number') {
      context.commit('setFinalizedCount', session.finalizedCount)
    }
    if (typeof session.archivedCount === 'number') {
      context.commit('setArchivedCount', session.archivedCount)
    }
    if (
      Object.prototype.hasOwnProperty.call(session, 'reconciliationStatFilter')
    ) {
      let filter = session.reconciliationStatFilter
      if (filter === 'conciliados' || filter === 'pareados') {
        filter = 'revisao'
      }
      context.commit('setReconciliationStatFilter', filter)
    }
    if (session.reconciliationPairingUi) {
      context.commit(
        'setReconciliationPairingUi',
        session.reconciliationPairingUi
      )
    }
    if (Array.isArray(session.archivedFromExtract)) {
      context.commit('setArchivedFromExtract', session.archivedFromExtract)
    }

    // Não reaplica totais do summary: podem estar desatualizados após pareamentos.
    // Os totalizadores são recalculados em Automatic.vue a partir de importedOFX.data.
    return true
  } catch (e) {
    console.error('Erro ao restaurar sessão de conciliação:', e)
    return false
  }
}

function clearReconciliationSession(context) {
  try {
    localStorage.removeItem(RECONCILIATION_SESSION_KEY)
  } catch (e) {
    console.error('Erro ao limpar sessão de conciliação:', e)
  }
  if (context && context.commit) {
    context.commit('resetReconciliationPairingUi')
  }
}

function pairCreatedBillWithReconciliation(
  context,
  { importedId, systemTransaction }
) {
  const importedOFX = context.state.importedOFX
  if (!importedOFX?.data || !importedId || !systemTransaction) return
  const normalizedSystemTransaction = {
    ...systemTransaction,
    reconciled: false,
    reconciled_at: null,
    reconciled_by: null,
    reconciliation_type: null
  }
  const data = [...importedOFX.data]
  const idx = data.findIndex(
    item => item?.imported_transaction?.id === importedId
  )
  if (idx === -1) return
  const updatedItem = {
    ...data[idx],
    system_transaction: normalizedSystemTransaction
  }
  data.splice(idx, 1, updatedItem)
  context.commit('setImportedOFX', { ...importedOFX, data })
  persistReconciliationSession(context)
}

async function fetchArchivedEntries(context) {
  const bankAccountId = context.state.ofxForm?.bank_account_id
  if (!bankAccountId) return
  const io = context.state.importedOFX
  const period = context.state.ofxForm?.period
  const start = io?.start ?? period?.start ?? null
  const end = io?.end ?? period?.end ?? null
  try {
    const { data } = await api.getArchivedReconciliationEntries(
      bankAccountId,
      start,
      end
    )
    context.commit('setArchivedEntriesFromApi', data?.data ?? data ?? [])
  } catch (e) {
    context.commit('setArchivedEntriesFromApi', [])
  }
}

export default {
  getClinicBankAccounts,
  getClinicPaymentMethods,
  importOFX,
  getTransactions,
  reconcileTransactions,
  unreconcileTransactions,
  updateOFXFormField,
  updateOFXFormPeriod,
  updateOFXFormFile,
  resetOFXForm,
  initializeOFXForm,
  setIsAutomaticReconciliation,
  // exported for views
  persistReconciliationSession,
  restoreReconciliationSession,
  clearReconciliationSession,
  pairCreatedBillWithReconciliation,
  fetchArchivedEntries
}
